import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {FuseModule} from '../@fuse/fuse.module';
import {FuseSharedModule} from '../@fuse/shared.module';
import {FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule} from '../@fuse/components';
import {fuseConfig} from './fuse-config';
import {LayoutModule} from './layout/layout.module';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatLuxonDateModule} from 'ngx-material-luxon';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TranslateModule} from '@ngx-translate/core';
import {TorchBackendModule} from '../@backend/torch-backend.module';
import {torchConfig} from './torch-config';
import {NgxDnDModule} from '@swimlane/ngx-dnd';
import {MaterialExtensionsModule} from '@ng-matero/extensions';
import {PageTitleService} from './shared/services/page-title.service';
import {NgxPermissionsModule, NgxPermissionsService} from 'ngx-permissions';
import {TorchFilesService, TorchTokenService, TorchUser, TorchUserService} from '../@backend/modules';
import {NgxFileDropModule} from 'ngx-file-drop';
import {Angulartics2, Angulartics2Module} from 'angulartics2';
import {ToastrModule} from 'ngx-toastr';
import {AuthInterceptor} from './interceptors/auth.interceptor';
import {SharedModule} from './shared/shared.module';
import {SessionService} from './shared/services/session.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {GlobalErrorHandler} from './errors/global-error.handler';
import {HttpErrorInterceptor} from './errors/http-error.interceptor';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import {MSALGuardConfigFactory, MSALInstanceFactory} from './msal-config';
import {filter, map, switchMap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {QueryFiltersModule} from './shared/modules/query-filters/query-filters.module';
import {SoftwareUpdateService} from './shared/services/software-update.service';
import {AlertWrapperComponent} from './shared/components/alert-wrapper/alert-wrapper.component';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService, MsalGuard,
  MsalService
} from '@azure/msal-angular';
import {RouteReuseStrategy} from '@angular/router';
import {RouteReusableStrategy} from './shared/route-reusable-strategy';
import {MatDatepickerModule} from '@angular/material/datepicker';

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    ServiceWorkerModule.register('/ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerWhenStable:5000'
    }),
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    TranslateModule.forRoot(),
    MatLuxonDateModule,
    MatButtonModule,
    MatIconModule,
    NgxDnDModule.forRoot(),
    FuseModule.forRoot(fuseConfig),
    TorchBackendModule.forRoot(torchConfig),
    FuseProgressBarModule,
    FuseSharedModule,
    FuseSidebarModule,
    FuseThemeOptionsModule,
    MaterialExtensionsModule,
    LayoutModule,
    NgxPermissionsModule.forRoot(),
    NgxFileDropModule,
    SharedModule.forRoot(),
    Angulartics2Module.forRoot(),
    ToastrModule.forRoot({
      timeOut: 7500,
      extendedTimeOut: 1000,
      positionClass: 'toast-top-right',
      progressBar: true,
      closeButton: true,
      progressAnimation: 'decreasing',
      enableHtml: true,
    }),
    MatProgressSpinnerModule,
    MatDatepickerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (
        userService: TorchUserService,
        permissionsService: NgxPermissionsService,
        auth: MsalService,
        session: SessionService
      ) => () => new Promise(resolve => {
          let observable: Observable<boolean>;

          if (auth.instance.getActiveAccount()) {
            observable = userService.currentTorchUser.pipe(
              map((user: TorchUser | null) => {
                if (!!user && !!user.settings) {
                  session.updateSettings(user.settings);
                  permissionsService.loadPermissions(user.permissions.map(p => p.permission));
                  return true;
                }
              })
            );
          } else {
            observable = auth.handleRedirectObservable().pipe(
              filter(r => !!r || !!auth.instance.getActiveAccount()),
              switchMap(() => userService.currentTorchUser),
              map((user: TorchUser | null) => {
                if (!!user && !!user.settings) {
                  session.updateSettings(user.settings);
                  permissionsService.loadPermissions(user.permissions.map(p => p.permission));
                  return true;
                }
              })
            );
          }

          observable.subscribe(result => resolve(result));
        }
      ),
      deps: [TorchUserService, NgxPermissionsService, MsalService, SessionService],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor, multi: true
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy
    },
    PageTitleService,
    TorchFilesService,
    Angulartics2,
    SessionService,
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    TorchTokenService,
    QueryFiltersModule,
    SoftwareUpdateService,
  ],
  bootstrap: [
    AppComponent,
    AlertWrapperComponent
  ]
})
export class AppModule {
}
